import { DASHBOARDS_PAGE_KEY } from './constants';
import type { TTranslations } from './types';

// TODO: l10n review — translations are functional equivalents of the Spanish source
export const en: TTranslations = {
  [DASHBOARDS_PAGE_KEY]: {
    // Page header
    PAGE_TITLE: 'Dashboards',
    PAGE_SUBTITLE:
      'Visualize the general pulse of your organization across its main active modules.',
    // Header actions
    FRESHNESS_PREFIX: 'Last updated:',
    FRESHNESS_REFRESH_LABEL: 'Refresh dashboard',

    // Product selector
    PRODUCT_SELECTOR_LABEL: 'Product',
    PRODUCT_SELECTOR_HELPER: 'Select the product you want to analyze',
    PRODUCT_GENERAL: 'General',
    PRODUCT_LEARNING: 'Learning',

    // Section tags
    SECTION_A_PERIOD_TAG: 'Last 30 days',
    SECTION_C_PERIOD_TAG: 'Current month',
    SECTION_C_PERIOD_TOOLTIP:
      'Activity for the current month, counted from the first day of the month to today. Does not represent the last 30 calendar days.',

    // Section headings
    SECTION_A_HEADING: 'Key metrics',
    SECTION_B_HEADING: 'Trends',
    SECTION_C_HEADING: 'Module pulse',
    SECTION_D_HEADING: 'Recommended actions',
    SECTION_E_HEADING: 'Month highlights',
    SECTION_E_HEADING_ORG: 'Organization highlights',

    // Sections
    SECTION_PLACEHOLDER: 'Content coming soon',

    // Section A — KPI cards
    SECTION_A_CARD_1_DESC: 'Licenses in use',
    SECTION_A_CARD_1_HELP:
      'Licenses in use out of the total contracted. Active: users who logged in at least once and consume a license. Pending: users who consume a license, but have not activated their account yet. Available: contracted licenses not yet assigned. Source: User management.',
    SECTION_A_CARD_2_DESC: '% Employees with login in the last 30 days',
    SECTION_A_CARD_2_HELP:
      'Percentage of active employees who logged in at least once in the last 30 days. It is the platform’s real adoption proxy. The delta compares against the same calculation from the previous month.',
    SECTION_A_CARD_3_DESC: 'Headcount growth last 3 months',
    SECTION_A_CARD_3_HELP:
      'Net difference in employees: current headcount vs 3 months ago (additions minus departures). The sparkline shows monthly activations over the last 12 months. Source: HRIS (Centralized).',

    // Section B — Trend cards
    SECTION_B_CARD_1_TITLE: 'Platform activity',
    SECTION_B_CARD_1_DESC: 'Unique users per day · Login events (Centralized)',
    SECTION_B_CARD_1_HELP:
      'Unique users who logged in each day (login events · Centralized). Compares the current period against the previous one; days with no activity count as 0. Average, peak and minimum are calculated over the current period.',
    SECTION_B_CARD_2_TITLE: 'Activity composition',
    SECTION_B_CARD_2_DESC: 'Actions by module · delta vs previous month',
    SECTION_B_CARD_2_HELP:
      'Actions recorded by each module during the period, with their variation vs the previous period. Each bar is calculated independently using its own module’s activity data.',
    SECTION_B_MONTH_FILTER: 'May 2026',

    // Section C — Module cards
    SECTION_C_MODULE_LEARNING: 'Learning',
    SECTION_C_MODULE_TALENT: 'Talent',
    SECTION_C_MODULE_RECRUITMENT: 'Recruitment',
    SECTION_C_MODULE_COMMUNICATIONS: 'Communications',
    SECTION_C_MODULE_LEARNING_HELP:
      'Overall progress = average progress of assigned courses. Completed courses sums own catalog + Crehana for the month. Satisfaction = average (1–5) of end-of-course surveys for the period. Same source as the centralized reports.',
    SECTION_C_MODULE_TALENT_HELP:
      'Active processes = all processes in progress or started (Competencies, Goals, Climate, OKRs and PID), regardless of when they were created. Feedback = completed during the period.',
    SECTION_C_MODULE_RECRUITMENT_HELP:
      'Open positions = positions currently published. Candidates in process = unique applicants per position (not duplicated per recruiter).',
    SECTION_C_MODULE_COMMUNICATIONS_HELP:
      'Monthly activity of the Communications module: posts for the period and interactions (reactions + comments).',

    // Section D — Recommended actions
    SECTION_D_ALERTS_TITLE: 'Alerts that need attention',
    SECTION_D_ALERTS_HELP:
      'Actionable counts calculated live and ordered by severity: mandatory courses overdue or expiring soon with low progress, evaluations with low participation or pending leader sign-off, assignments not started (+14 days), pending vacations and scheduled reports running soon.',
    SECTION_D_FILTER_ALL: 'All',

    // Section E — Highlight cards
    SECTION_E_CARD_1_TITLE: 'Top 5 most completed courses',
    SECTION_E_CARD_1_DESC: 'This month · Apr 14 – May 14',
    SECTION_E_CARD_1_HELP:
      'Completed = the employee finished 100% of the course modules in the period. Hours reflect actual playback (minutes of progress), not the course duration. For final certification, see the Certificates issued milestone.',
    SECTION_E_CARD_2_TITLE: 'Areas with most active employees',
    SECTION_E_CARD_2_DESC: '% employees with login last 30 days · This month',
    SECTION_E_CARD_2_DESC_NEUTRAL: '% of employees with login',
    SECTION_E_CARD_2_HELP:
      'Percentage of employees in each area with at least one login in the last 30 days, ordered from highest to lowest adoption. Uses the same calculation as the login KPI at the top, segmented by area.',
    SECTION_E_CARD_3_TITLE: 'Milestones achieved this month',
    SECTION_E_CARD_3_DESC: 'Achievements for the period Apr 14 – May 14',
    SECTION_E_CARD_3_HELP:
      'Achievements for the period compared against the previous month: study hours, certificates issued, evaluation processes closed, PIDs generated and new licenses activated.',

    // Empty state
    EMPTY_TITLE: "You don't have any modules to display yet",
    EMPTY_CTA: 'Contact sales',

    // Error state
    ERROR_BANNER: 'We could not load the dashboard.',
    ERROR_RETRY: 'Retry',

    // Recommended actions states
    RECOMMENDED_EMPTY: 'There are no recommended actions for now.',
    RECOMMENDED_NO_ALERTS: 'You have no pending alerts',
    RECOMMENDED_ERROR: 'We could not load the recommended actions.',

    // Recommended actions — alert titles
    ALERT_OVERDUE_COURSES_TITLE: 'Overdue mandatory courses',
    ALERT_EXPIRING_COURSES_TITLE:
      'Mandatory courses expiring soon with low progress',
    ALERT_LOW_EVAL_PARTICIPATION_TITLE:
      'Evaluation processes with low participation',
    ALERT_PENDING_CALIBRATION_TITLE: 'Evaluations pending leader sign-off',
    ALERT_UNSTARTED_PATHS_TITLE:
      'Assigned paths/courses not started (over 14 days)',
    ALERT_LEADERS_NO_EVAL_TITLE: 'Leaders with teams not evaluated this cycle',
    ALERT_PENDING_VACATIONS_TITLE:
      'Pending vacation requests awaiting approval',
    ALERT_UPCOMING_REPORTS_TITLE: 'Scheduled reports running soon',

    // Recommended actions — alert sublabels ({{count}} interpolated)
    ALERT_OVERDUE_COURSES_SUBLABEL:
      '{{count}} collaborators with overdue mandatory courses · Compliance impact',
    ALERT_EXPIRING_COURSES_SUBLABEL:
      '{{count}} collaborators have mandatory courses expiring in under 7 days with less than 50% progress · Time to act',
    ALERT_LOW_EVAL_PARTICIPATION_SUBLABEL:
      '{{count}} Q2 processes with less than 50% response rate 7 days before close · Performance + Climate',
    ALERT_PENDING_CALIBRATION_SUBLABEL:
      '{{count}} evaluations awaiting final leader calibration · 6 days to quarter close',
    ALERT_UNSTARTED_PATHS_SUBLABEL:
      '{{count}} collaborators with assigned paths that have not started · Consider re-activating',
    ALERT_LEADERS_NO_EVAL_SUBLABEL:
      '{{count}} leaders have not sent team evaluations · Still pending for Q2 cycle',
    ALERT_PENDING_VACATIONS_SUBLABEL:
      '{{count}} requests in your org · Leader approval (visibility only)',
    ALERT_UPCOMING_REPORTS_SUBLABEL:
      '{{count}} reports run in the next 24h · Review recipients and filters',

    // Permission
    TOAST_NO_PERMISSION: "You don't have permission to view Dashboards",

    // CTA strip
    CTA_TITLE: 'Want to go deeper?',
    CTA_SUBTITLE: 'Generate a custom report from Tracking in Reports.',
    CTA_BUTTON: 'Create custom report',

    // KPI card body
    KPI_DELTA_WOW: 'vs previous week',
    KPI_DELTA_MOM: 'vs previous month',
    KPI_DELTA_NET: 'net last 3 months',
    KPI_NET_LABEL: 'net collaborators',
    KPI_ERROR: 'Could not load this metric.',
    KPI_SPARKLINE_A11Y: 'Trend for the last 30 days',

    // Activity composition card (ANLT-168)
    // title + subtitle are BE-authoritative (sent in response.title / response.subtitle)
    ACTIVITY_COMPOSITION_LOADING_LABEL: 'Loading activity composition',
    ACTIVITY_COMPOSITION_ERROR: 'We could not load the activity composition.',
    ACTIVITY_COMPOSITION_RETRY: 'Retry',
    ACTIVITY_COMPOSITION_EMPTY: 'No additional contracted modules',
    ACTIVITY_COMPOSITION_SUBHEADING: 'High-volume actions',

    // Module pulse card footer
    MODULE_VER_DETALLE: 'View detail',

    // Activity chart series
    ACTIVITY_SERIES_CURRENT: 'Current month',
    ACTIVITY_SERIES_PREVIOUS: 'Previous month',

    // Activity stats bar
    ACTIVITY_STAT_AVG: 'Average',
    ACTIVITY_STAT_AVG_UNIT: 'users/day',
    ACTIVITY_STAT_PEAK: 'Peak',
    ACTIVITY_STAT_MIN: 'Minimum',
    ACTIVITY_DELTA_LABEL: 'vs previous month',

    // Top courses ranking — metrics
    TOP_COURSES_COMPLETED: '{{count}} completed',
    TOP_COURSES_AVG_HOURS: '{{hours}} h avg',
    // Top courses ranking — states
    TOP_COURSES_TOOLTIP:
      'Completed = collaborator finished 100% of the course modules',
    TOP_COURSES_EMPTY: 'No courses completed this month yet',
    TOP_COURSES_ERROR: 'We could not load the courses ranking',
    TOP_COURSES_RETRY: 'Retry',

    // Areas engagement bars — states
    AREAS_ENGAGEMENT_EMPTY: 'No area activity data yet',
    AREAS_ENGAGEMENT_ERROR: 'We could not load the area activity',
    AREAS_ENGAGEMENT_RETRY: 'Retry',

    // Tooltip accessibility
    HELP_TOOLTIP_LABEL: 'More information',

    // Section E — Milestones
    // TODO: l10n review
    MILESTONES_TITLE: 'Monthly achievements',
    MILESTONES_TITLE_NEUTRAL: 'Achievements',
    MILESTONES_PERIOD: 'Achievements from Apr 14 – May 14',
    MILESTONE_STUDY_HOURS: 'study hours completed',
    MILESTONE_CERTIFICATIONS: 'certificates issued',
    MILESTONE_TALENT_PROCESSES: 'evaluation processes closed',
    MILESTONE_PIDS: 'PIDs generated',
    MILESTONE_LICENSES: 'new licenses activated',
    MILESTONES_ERROR: 'We could not load the achievements.',
    MILESTONES_RETRY: 'Retry',

    // License usage card (ANLT-451)
    KPI_LICENSES_SUBTEXT: 'of {{total}} contracted',
    USAGE_BAR_A11Y: 'Percentage of licenses in use',
    LICENSE_LEGEND_ACTIVE: 'Active',
    LICENSE_LEGEND_PENDING: 'Pending',
    LICENSE_LEGEND_AVAILABLE: 'Available',
  },
};
